import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Form,
  Input,
  Row,
  Col,
  Modal,
  message,
  // Tooltip,
  Typography,
  TimePicker,
  DatePicker,
} from 'antd';
import BusquedaAvanzadaUsers from 'components/BusquedaAvanzadaCajeros';
import { getUsuario } from 'api/usuarios/usuarios';
import { InputSearchPro } from 'components/InputSearch';
import ModalDelete from 'components/ModalDelete';
import getFormatedValues, {
  TIME_FORMAT, DATE_FORMAT,
} from 'utils/formatValues';
import Uploader from 'components/Uploader';
import FormSubmitControls from 'components/FormSubmitControls';
import {
  getVerificados,
  deleteVerificado,
  patchVerificado,
  postVerificado,
  getUsuariosInspectores,
  getArchivoVerificado,
} from 'api/empresas/direccionVerificada';
import API from 'utils/api';
import { onError, onSuccess } from 'utils/handlers';
import Select from 'components/Select';
import Table from 'components/Table';
import moment from 'moment';
import { getModelPermission } from 'utils/permission';
import { getEstadosDeDireccionesVerificadas } from 'api/empresas';

export const modelCrud = 'direccionverificadadecontribuyente';

const DireccionVerificada = ({ direccionId, mutable }) => {
  const permission = useMemo(() => getModelPermission(modelCrud), []);
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [visible, setVisible] = useState(false);
  const [estadosVerificacion, setEstadosVerificacion] = useState([]);
  const [visibleAdvancedUser, setVisibleAdvancedUser] = useState(false);
  const [userFound, setUserFound] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTime, setSelectedTime] = useState(null);
  const [archivoVerificacion, setArchivoVerificacion] = useState([]);
  const allowedExtensions = ['pdf', 'jpg', 'jpeg', 'png'];

  const fetchData = async (page = 1, q = null) => {
    setLoading(true);
    setCurrentPage(page);
    const _data = await getVerificados({
      page,
      ...q,
      direccion: direccionId,
    });
    form.setFieldsValue({ direccion: direccionId });
    setData(_data.results);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      const _estados = await getEstadosDeDireccionesVerificadas();
      if (mounted) {
        setEstadosVerificacion(_estados);
      }
    };
    fetch();
    return () => { mounted = false; };
  }, []);

  const fetchUser = async (id) => {
    setLoading(true);
    if (id) {
      const _user = await getUsuario(id?.id || id);
      if (_user) {
        form.setFieldsValue({
          usuario_inspector: _user.id,
        });
        const matched = {
          ..._user,
          nombre_completo: `${_user?.first_name} ${_user?.last_name
            || ''} ${_user?.second_last_name || ''}`,
        };
        setUserFound(matched);
      } else {
        message.info('No se encontró usuario');
      }
    }
    setLoading(false);
  };

  const onCancel = () => {
    form.resetFields();
    form.resetFields(['q']);
    setUserFound([]);
    setVisible(false);
    setSelectedRowKeys([]);
  };

  const saveArchivo = async (values) => {
    const { archivo } = values;

    if (archivo) {
      const formData = new FormData();
      let ID_ARCHIVO = null;

      formData.append('archivo', archivo.fileList[0].originFileObj);
      formData.append('descripcion', 'comprobante de verificación de domicilio');
      formData.append('direccion_verificada', values.id);
      formData.append('extension_del_documento', archivo.fileList[0].name.split('.').pop());
      formData.append('tamanio', archivo.fileList[0].size);

      if (archivoVerificacion?.id) {
        ID_ARCHIVO = await API.patch(
          `/empresas/archivos-de-direcciones-verificadas/${archivoVerificacion.id}`,
          formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          },
        );
      } else {
        ID_ARCHIVO = await API.post(
          '/empresas/archivos-de-direcciones-verificadas/',
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        );
      }

      return ID_ARCHIVO.data.id;
    }
    return null;
  };

  const patchDireccionVerificada = async (id, values) => {
    try {
      let res = null;
      const { archivo } = values;
      if ((archivoVerificacion?.response !== archivo)) {
        const ID_ARCHIVO = await saveArchivo({ ...values, id });
        res = await patchVerificado(id, { ...values, comprobante: ID_ARCHIVO });
        setArchivoVerificacion([]);
      } else {
        res = await patchVerificado(id, values);
      }

      if (res) {
        onSuccess();
        onCancel();
        await fetchData();
      } else {
        message.error('No se ha podido guardar', res);
      }
    } catch (err) {
      onError(err);
    }
  };

  const postDireccionVerificada = async (values) => {
    try {
      const res = await postVerificado({ ...values });

      if (res) {
        if (values?.archivo?.fileList?.length) {
          const ID_ARCHIVO = await saveArchivo({ ...values, id: res.id });
          await patchVerificado(res.id, { comprobante: ID_ARCHIVO });
          setArchivoVerificacion([]);
        }
        onSuccess();
        onCancel();
        await fetchData();
      } else {
        message.error('No se ha podido guardar', res);
      }
    } catch (err) {
      onError(err);
    }
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;

      values.es_ultimo_registro = true;
      values.direccion = direccionId;
      values.hora_verificacion_domicilio = selectedTime.format(TIME_FORMAT);
      values.fecha_verificacion_domicilio = values.fecha_verificacion_domicilio.format(DATE_FORMAT);

      const formattedvalues = getFormatedValues(values);

      if (key) {
        await patchDireccionVerificada(key, formattedvalues);
      } else {
        await postDireccionVerificada(formattedvalues);
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = async (record) => {
    setSelectedRowKeys([record.id]);
    let archivo = null;
    if (record?.comprobante) {
      const res = await getArchivoVerificado(record.comprobante);
      archivo = res.archivo || null;
      setArchivoVerificacion({ response: res.archivo, id: record.comprobante });
    }
    form.setFieldsValue({
      ...record,
      archivo,
      observaciones: record.observaciones,
      usuario: record.usuario?.id,
      hora_verificacion_domicilio: record.hora_verificacion_domicilio
        ? moment(record.hora_verificacion_domicilio, TIME_FORMAT)
        : null,
      fecha_verificacion_domicilio: record.fecha_verificacion_domicilio
        ? moment(record.fecha_verificacion_domicilio, DATE_FORMAT)
        : null,
    });
  };

  const onClickEdit = async () => {
    const key = selectedRowKeys[0];
    const inspectorId = await data.find((item) => item.id === key)?.usuario_inspector;
    setSelectedTime(
      moment(data.find((item) => item.id === key)?.hora_verificacion_domicilio, TIME_FORMAT),
    );
    // Get usuario_inspector
    const match = await getUsuario(inspectorId?.id || inspectorId);
    if (match) {
      await fetchUser(match?.id);
      form.setFieldsValue({
        nombre_completo: match?.first_name || '',
      });
    }
    setVisible(true);
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const deleteItem = async () => {
    setLoading(true);
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteVerificado(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
    }
    setLoading(false);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const reset = () => {
    setUserFound([]);
    form.resetFields(['q']);
  };

  const searchUsuario = async () => {
    try {
      setLoading(true);
      await form.validateFields(['q']);
      const { q: email } = form.getFieldsValue();
      const resUser = await getUsuariosInspectores({ email });

      if (resUser.length) {
        form.setFieldsValue({
          usuario: resUser[0].id,
        });
        const _usuario = resUser.map((e) => ({
          ...e,
          nombre_completo: `${e.first_name} ${e.last_name || ''} ${e.second_last_name || ''}`,
        }));
        setUserFound([_usuario[0]]);
      } else {
        message.info('No se encontraron coincidencias');
        setUserFound([]);
      }
      setLoading(false);
    } catch (error) {
      onError(error, setLoading);
    }
  };

  const handleTimeChange = (time) => {
    setSelectedTime(time);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const rules = {
    required: [{ required: true }],
  };

  const columns = [
    {
      titleText: 'Estado de verificación',
      dataIndex: 'estatus_de_direccion_verificada',
      key: 'estatus_de_direccion_verificada',
      render: (text) => estadosVerificacion.find((e) => e?.id === text)?.descripcion,
    },
    {
      titleText: 'Fecha',
      dataIndex: 'fecha_verificacion_domicilio',
      key: 'fecha_verificacion_domicilio',
      render: (text) => moment(text).format(DATE_FORMAT),
    },
    {
      titleText: 'Hora',
      dataIndex: 'hora_verificacion_domicilio',
      key: 'hora_verificacion_domicilio',
      render: (text) => moment(text, TIME_FORMAT).format(TIME_FORMAT),
    },
    {
      titleText: 'Inspector',
      dataIndex: 'usuario_inspector',
      key: 'usuario_inspector',
      render: (text) => `${text?.first_name || ''} ${text?.last_name || ''}` || 'No asignado',
    },
    {
      titleText: 'Observaciones',
      dataIndex: 'observaciones',
      key: 'observaciones',
    },
  ];

  return (
    <>
      <Col span={24}>
        <Row gutter={10}>
          <Col className="gutter-row" md={24} sm={24} xs={24}>
            <Typography.Title level={3}>Dirección Verificada</Typography.Title>
            <Row gutter={10}>
              <Table
                cols={columns}
                data={data}
                rowSelection={rowSelection}
                handleOnRowClick={handleOnRowClick}
                controls={{
                  onClickAdd,
                  onClickEdit,
                  onClickDelete,
                }}
                noControls={mutable}
                allowSort={false}
                allowSearch={false}
                rowKey="id"
                permission={permission}
                paginationCallback={fetchData}
                currentPage={currentPage}
                count={data.count}
                hasAllViewPermission
              />
              <Modal
                open={visible}
                closable={false}
                footer={null}
                title={(
                  <FormSubmitControls
                    label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Verificación`}
                    onFinish={onFinish}
                    onCancel={onCancel}
                    loading={loading}
                  />
                )}
                keyboard={!loading}
                maskClosable={!loading}
                onCancel={onCancel}
                forceRender
              >
                <Form
                  name="verificada"
                  form={form}
                  layout="vertical"
                  onFinish={() => onFinish()}
                >
                  <Col xs={24} sm={24} md={24}>
                    <Form.Item
                      name="estatus_de_direccion_verificada"
                      label="Estado de verificación"
                      hasFeedback
                      rules={rules.required}
                    >
                      <Select dataSource={estadosVerificacion} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={24}>
                    <Form.Item
                      name="fecha_verificacion_domicilio"
                      label="Fecha de verificación"
                      hasFeedback
                      rules={rules.required}
                    >
                      <DatePicker
                        placeholder=""
                        format={DATE_FORMAT}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={24}>
                    <Form.Item
                      name="hora_verificacion_domicilio"
                      label="Hora de verificación"
                      hasFeedback
                      rules={rules.required}
                    >
                      <TimePicker
                        format={TIME_FORMAT}
                        onChange={handleTimeChange}
                        value={selectedTime}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={24}>
                    <InputSearchPro
                      label="Inspector"
                      tooltip="Búsqueda por Correo del Inpsector"
                      name="q"
                      rules={rules.required}
                      onClickAdvanced={() => setVisibleAdvancedUser(true)}
                      onPressEnter={() => searchUsuario()}
                      breakPoints={{
                        md: 24,
                      }}
                      resultProps={{
                        rules: rules.required,
                        name: 'usuario_inspector',
                        label: 'Usuario Inspector',
                        dataSource: userFound,
                        labelProp: 'nombre_completo',
                        onClear: () => reset(),
                        breakPoints: {
                          md: 24,
                        },
                      }}
                    />
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      name="observaciones"
                      label="Observaciones"
                    >
                      <Input.TextArea
                        autoSize={{ minRows: 2, maxRows: 3 }}
                        maxLength={250}
                        allowClear
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Uploader
                      title="comprobante de verificación de domicilio"
                      limitMB={5}
                      allowPreview
                      allowedExtensions={allowedExtensions}
                      formItemName="archivo"
                      key={form.getFieldValue('archivo')}
                      file={form.getFieldValue('archivo')}
                      onError={onError}
                      tooltip={{
                        title: `Extensiones Permitidas para el Documento: ${allowedExtensions}`,
                      }}
                      fullSize
                    />
                  </Col>
                </Form>
              </Modal>
              <ModalDelete
                onDelete={deleteItem}
                onCancel={() => setVisibleAlert(false)}
                visible={visibleAlert}
                content="Dirección verificada seleccionada"
                loading={loading}
              />
              <BusquedaAvanzadaUsers
                visible={visibleAdvancedUser}
                setVisible={setVisibleAdvancedUser}
                callback={fetchUser}
                dataExterno={[]}
                setDataExterno={() => { }}
                isInspector
              />
            </Row>
          </Col>
        </Row>
      </Col>
    </>
  );
};

DireccionVerificada.propTypes = {
  direccionId: PropTypes.number,
  mutable: PropTypes.bool,
};

DireccionVerificada.defaultProps = {
  direccionId: null,
  mutable: true,
};

export default DireccionVerificada;
